import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';

interface Participant {
  id: string;
  name: string;
  status: 'active' | 'last-seen' | 'inactive';
  awayFor?: string;
}

interface ParticipantsActivityListProps {
  users: Participant[];
  className?: string;
}

const statusDot: Record<string, string> = {
  active: 'tw:bg-green-600 tw:border-green-600',
  'last-seen': 'tw:bg-white tw:border-green-600',
  inactive: 'tw:bg-red-700 tw:border-red-700',
};

export default function ParticipantsActivityList({
  users,
  className,
}: ParticipantsActivityListProps) {
  const { formatMessage } = useIntl();

  const statusLabel = (u: Participant) =>
    u.status === 'last-seen'
      ? formatMessage(
          {
            id: 'participant-status-last-seen',
            defaultMessage: 'Seen {awayFor} ago',
          },
          { awayFor: u.awayFor }
        )
      : u.status === 'active'
        ? formatMessage({
            id: 'participant-status-active',
            defaultMessage: 'Active',
          })
        : formatMessage({
            id: 'participant-status-inactive',
            defaultMessage: 'Inactive',
          });

  const sorted = [...users].sort((a, b) => a.name.localeCompare(b.name));
  const viewed = users.filter((u) => u.status !== 'inactive').length;
  const activeNow = users.filter((u) => u.status === 'active').length;

  return (
    <div className={cn('tw', className)}>
      <p className="tw:m-0 tw:font-bold tw:text-gray-900 tw:text-sm tw:pb-0">
        <FormattedMessage
          id="tender-details-activity-modal-header"
          defaultMessage="Activity"
        />
      </p>
      <p className="tw:m-0 tw:mt-0.5 tw:text-xs tw:text-gray-500 tw:pb-0">
        <FormattedMessage
          id="tender-details-activity-stats"
          defaultMessage="{viewed} Viewed  |  {activeNow} Active now"
          values={{ viewed, activeNow }}
        />
      </p>
      <hr className="tw:my-2 tw:border-gray-200" />
      <ul
        className={cn(
          'tw:list-none tw:p-0 tw:m-0 tw:grid tw:grid-cols-1',
          sorted.length > 20 && 'tw:grid-cols-2',
          'tw:gap-x-4 tw:max-h-[60vh] tw:overflow-y-auto'
        )}
      >
        {sorted.map((u) => (
          <li
            key={u.id}
            className={cn(
              'tw:flex tw:gap-1.5 tw:text-xs tw:leading-4 tw:py-0.5 tw:px-0',
              u.status === 'active' && 'tw:font-bold'
            )}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    'tw:inline-block tw:w-[7px] tw:h-[7px] tw:rounded-full tw:border tw:shrink-0 tw:mt-1',
                    statusDot[u.status]
                  )}
                />
              </TooltipTrigger>
              <TooltipContent>{statusLabel(u)}</TooltipContent>
            </Tooltip>
            <span>{u.name}</span>
            {u.status === 'last-seen' && u.awayFor && (
              <span className="tw:text-[0.6rem] tw:text-gray-500 tw:ml-auto tw:shrink-0">
                {u.awayFor}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
